import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { AiOutlineFire } from 'react-icons/ai'
import { BsFloppy, BsPencil, BsPin, BsXLg } from 'react-icons/bs'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Dialog, Disclosure, Transition } from '@headlessui/react'
import {
  ArrowPathRoundedSquareIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  Bars3Icon,
  BellAlertIcon,
  BriefcaseIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  CubeIcon,
  HomeIcon,
  LockClosedIcon,
  PresentationChartBarIcon,
  RectangleGroupIcon,
  ShoppingBagIcon,
  TicketIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessPackagesQuery } from '@/hooks/business/useBusinessPackagesQuery'
import { RoleCode } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'

import useServer from '../../api/useServerMain'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { changeStaticBar } from '../../store/slices/sessionSlice'
import Fetching from '../misc/Fetching'
import ImageComponent from '../misc/Images/Image'
import Modal from '../modals/GenericModal'
import { Switch } from '../ui/switch'
import BranchesModal from './BranchesModal'
import { convertBoolean } from '@/utils/translate'

interface SideBarProps {
  barState: boolean
  switchSideBar: () => void
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface NavChildren {
  name: string
  href: string
  current: boolean
  block?: boolean
}

interface NavBadge {
  name: string
  bgColor: string
  textColor: string
}

interface NavItem {
  name: string
  icon: React.ElementType
  current: boolean
  href?: string
  block?: boolean
  children?: NavChildren[]
  badge?: NavBadge
  section?: string
  id?: number
  isActive?: boolean
  isSensitive?: boolean
  parent?: string
  roles?: RoleCode[]
}

const SideBar = ({ barState, switchSideBar }: SideBarProps) => {
  const { pathname } = useLocation()
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessPackages, editBusinessPackages, refetchBusinessPackages } =
    useBusinessPackagesQuery({
      params: { businessId: businessData?.id ?? 0 },
      options: {
        enabled:
          !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
      },
    })
  const mainCurrent = pathname.split('/')[1]
  const secondaryCurrent = pathname.split('/')[2]
  const { business, branches } = useAppSelector((state) => state.init)
  const { staticBar } = useAppSelector((state) => state.session)
  const { allowRoles, denyRoles } = useServer()
  const plan = business?.subscriptionPlan.code
  const block = plan === 'FREE'

  const [showButtonSave, setShowButtonSave] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const navigate = useNavigate()

  const dispatch = useAppDispatch()

  // use_new_architecture
  const use_new_architecture = convertBoolean(
    business?.configurationsKey?.find(
      (item) => item.key === 'use_new_architecture'
    )?.value ?? ''
  )

  const moduleStates = useMemo(() => {
    const map = new Map<
      string,
      { id: number; isActive: boolean; isSensitive: boolean }
    >()
    businessPackages?.forEach((pkg) => {
      map.set(pkg.code, {
        id: pkg.id,
        isActive: pkg.isActive,
        isSensitive: pkg.isSensitive,
      })
    })
    return map
  }, [businessPackages])

  const isModuleActive = useCallback(
    (code: string) => {
      return businessPackages?.some((pkg) => pkg.code === code && pkg.isActive)
    },
    [businessPackages]
  )

  // const hasModule = useCallback(
  //   (code: string) => {
  //     if (showButtonSave) return true
  //     return businessPackages?.some((pkg) => pkg.code === code && pkg.isActive)
  //   },
  //   [businessPackages, showButtonSave]
  // )

  //NavItems
  // const navigation: NavItem[] = []
  const navigationEditable: NavItem[] = useMemo(() => {
    const navigation: NavItem[] = []

    //Home
    if (allowRoles(['OWNER', 'ADMIN'], true))
      navigation.push({
        name: 'Inicio',
        href: '/',
        icon: HomeIcon,
        current: mainCurrent === '',
        block,
        section: 'General',
        roles: ['OWNER', 'ADMIN'],
      })

    //Stocks
    if (
      allowRoles([
        'OWNER',
        'ADMIN',
        // 'MANAGER_SHIFT',
        // 'MANAGER_CHIEF',
        // 'MANAGER_SALES',
        'MANAGER_AREA',
        'STOCK_AREA_CHIEF',
      ])
    ) {
      const children: NavChildren[] = []

      children.push({
        name: 'Todos',
        href: `/stocks`,
        current: !secondaryCurrent && mainCurrent === 'stocks',
        block,
      })

      children.push({
        name: 'Movimientos',
        href: `/stocks/movements`,
        current: secondaryCurrent === 'movements' && mainCurrent === 'stocks',
        block,
      })

      // if (hasModule('F_DISPATCHES')) {
      children.push({
        name: 'Despachos',
        href: `/stocks/dispatches`,
        current: secondaryCurrent === 'dispatches' && mainCurrent === 'stocks',
        block,
      })

      children.push({
        name: 'Inventarios',
        href: `/stocks/inventories`,
        current: secondaryCurrent === 'inventories' && mainCurrent === 'stocks',
        block,
      })
      // }

      children.push({
        name: 'Análisis',
        href: `/stocks/analysis`,
        current: secondaryCurrent === 'analysis' && mainCurrent === 'stocks',
        block,
      })

      const code = 'M_INVENTORY'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Mis almacenes',
        icon: RectangleGroupIcon,
        current: mainCurrent === 'stocks',
        block,
        children,
        section: 'Gestión',
        roles: [
          'OWNER',
          'ADMIN',
          'MANAGER_SHIFT',
          'MANAGER_CHIEF',
          'MANAGER_SALES',
          'MANAGER_AREA',
          'STOCK_AREA_CHIEF',
        ],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    if (
      allowRoles(['OWNER', 'ADMIN', 'CARGO_MANAGER'], true) &&
      (showButtonSave || isModuleActive('M_LOGISTICS'))
    ) {
      const children: NavChildren[] = []

      children.push({
        name: 'Todas',
        href: `/load/receipt`,
        current: secondaryCurrent === 'receipt' && mainCurrent === 'load',
        block,
      })

      children.push({
        name: 'Análisis',
        href: `/load/analisis`,
        current: secondaryCurrent === 'analisis' && mainCurrent === 'load',
        block,
      })

      // if (allowRoles(["ADMIN", "ANALYSIS_REPORT", "MANAGER_AREA"]))
      //   children.push({
      //     name: "Análisis",
      //     href: `/load/analysis`,
      //     current: secondaryCurrent === "analysis" && mainCurrent === "load",
      //     block,
      //   });

      const code = 'M_LOGISTICS'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Cargas',
        icon: CubeIcon,
        current: mainCurrent === 'load',
        block,
        children,
        section: 'Gestión',
        roles: ['OWNER', 'ADMIN'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Logistica
    if (
      allowRoles(
        [
          'OWNER',
          'CARGO_MANAGER',
          'MANAGER_IMPORTER',
          'MANAGER_SALES_COMPANY',
          'OWNER_PROVIDER',
          'MANAGER_TRANSPORTATION',
          'TRANSPORTER',
          'MANAGER_STOCK_OPERATOR',
        ],
        true
      ) &&
      (showButtonSave || isModuleActive('M_LOGISTICS'))
    ) {
      const children: NavChildren[] = []

      // Mostrar cada item solo si el usuario tiene el rol correspondiente
      if (allowRoles(['OWNER', 'MANAGER_IMPORTER'], true))
        children.push({
          name: 'Notificación de embarque',
          href: block ? '#' : '/logistic/shipment-notifications',
          current: pathname.includes('/logistic/shipment-notifications'),
          block,
        })

      if (allowRoles(['OWNER', 'MANAGER_IMPORTER'], true))
        children.push({
          name: 'Expedientes',
          href: block ? '#' : '/logistic/files',
          current: pathname.includes('/logistic/files'),
          block,
        })

      if (
        allowRoles(
          [
            'OWNER',
            'MANAGER_IMPORTER',
            'MANAGER_SALES_COMPANY',
            'OWNER_PROVIDER',
          ],
          true
        )
      )
        if (
          allowRoles(
            [
              'OWNER',
              'MANAGER_IMPORTER',
              'MANAGER_TRANSPORTATION',
              'TRANSPORTER',
              'MANAGER_STOCK_OPERATOR',
              'MANAGER_SALES_COMPANY',
              'OWNER_PROVIDER',
            ],
            true
          )
        )
          children.push({
            name: 'Órdenes de compra',
            href: block ? '#' : '/logistic/purchase-orders',
            current: pathname.includes('/logistic/purchase-orders'),
            block,
          })

      children.push({
        name: 'Contenedores',
        href: block ? '#' : '/logistic/containers',
        current: pathname.includes('/logistic/containers'),
        block,
      })

      if (
        allowRoles(
          [
            'OWNER',
            'MANAGER_IMPORTER',
            'MANAGER_SALES_COMPANY',
            'OWNER_PROVIDER',
          ],
          true
        )
      )
        children.push({
          name: 'Inventario',
          href: block ? '#' : '/logistic/inventory',
          current: pathname.includes('/logistic/inventory'),
          block,
        })

      if (allowRoles(['OWNER', 'MANAGER_IMPORTER'], true))
        children.push({
          name: 'Compañías',
          href: block ? '#' : '/logistic/companies',
          current: pathname.includes('/logistic/companies'),
          block,
        })

      if (allowRoles(['OWNER', 'MANAGER_IMPORTER', 'OWNER_PROVIDER'], true))
        children.push({
          name: 'Categorías',
          href: block ? '#' : '/logistic/categories',
          current: pathname.includes('/logistic/categories'),
          block,
        })

      if (allowRoles(['OWNER', 'MANAGER_IMPORTER'], true))
        children.push({
          name: 'Origen/Destino',
          href: block ? '#' : '/logistic/origin-destiny',
          current: pathname.includes('/logistic/origin-destiny'),
          block,
        })

      if (allowRoles(['OWNER', 'MANAGER_TRANSPORTATION'], true))
        children.push({
          name: 'Vehículos',
          href: block ? '#' : '/logistic/vehicles',
          current: pathname.includes('/logistic/vehicles'),
          block,
        })

      const code = 'M_LOGISTICS'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Logística',
        icon: RectangleGroupIcon,
        current: mainCurrent === 'logistic',
        href: block ? '#' : 'logistic',
        section: 'Gestión',
        children,
        badge: {
          name: 'Beta',
          bgColor: 'bg-blue-500/20',
          textColor: 'text-blue-500',
        },
        roles: [
          'OWNER',
          'CARGO_MANAGER',
          'MANAGER_IMPORTER',
          'MANAGER_TRANSPORTATION',
          'TRANSPORTER',
          'MANAGER_STOCK_OPERATOR',
        ],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Products
    if (allowRoles(['OWNER', 'ADMIN', 'PRODUCT_PROCESATOR'], true)) {
      const children: NavChildren[] = []

      children.push(
        {
          name: 'Todos',
          href: `/products/all`,
          current: !secondaryCurrent && mainCurrent === 'products',
          block,
        },
        {
          name: 'Carta de venta',
          href: 'products/sales',
          current: secondaryCurrent === 'sales' && mainCurrent === 'products',
        },
        {
          name: 'Categorías de venta',
          href: 'products/sales_categories',
          current: secondaryCurrent === 'sales_categories',
        },
        {
          name: 'Categorías de almacén',
          href: 'products/general_categories',
          current: secondaryCurrent === 'general_categories',
          block,
        },
        {
          name: 'Cupones',
          href: block ? '#' : '/products/cupons',
          current: pathname.includes('/products/cupons'),
          block,
        },
        {
          name: 'Recursos',
          href: block ? '#' : '/products/resources',
          current: pathname.includes('/products/resources'),
          block,
        }
      )

      navigation.push({
        name: 'Mis productos',
        icon: ShoppingBagIcon,
        current: mainCurrent === 'products',
        children,
        section: 'Gestión',
        roles: ['OWNER', 'ADMIN', 'PRODUCT_PROCESATOR'],
      })
    }

    //Órdenes de Producción
    if (
      (showButtonSave || isModuleActive('M_PRODUCTION')) &&
      allowRoles(['OWNER', 'CHIEF_PRODUCTION'], true)
    ) {
      const children = [
        {
          name: 'Órdenes',
          href: '/orders',
          current: mainCurrent === 'orders' && !secondaryCurrent,
          block,
        },
        {
          name: 'Recetario',
          href: '/orders/recipes',
          current: mainCurrent === 'orders' && secondaryCurrent === 'recipes',
          block,
        },
        {
          name: 'Análisis',
          href: '/orders/analysis',
          current: mainCurrent === 'orders' && secondaryCurrent === 'analysis',
          block,
        },
      ]

      const code = 'M_PRODUCTION'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Producción',
        icon: AiOutlineFire,
        current: pathname.includes('/orders'),
        children,
        section: 'Gestión',
        roles: ['OWNER', 'CHIEF_PRODUCTION'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Suppliers
    if (
      (showButtonSave || isModuleActive('F_SUPPLIERS')) &&
      allowRoles(['OWNER', 'MANAGER_SUPPLIERS'], true)
    ) {
      const code = 'F_SUPPLIERS'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Proveedores',
        icon: TruckIcon,
        current: pathname.includes('/suppliers'),
        href: block ? '#' : '/suppliers',
        block,
        section: 'Gestión',
        roles: ['OWNER', 'MANAGER_SUPPLIERS'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Facturación
    if (!use_new_architecture) {
      if (
        (showButtonSave || isModuleActive('M_BILLING')) &&
        allowRoles([
          'OWNER',
          'ADMIN',
          'MANAGER_SALES',
          'MARKETING_SALES',
          'MANAGER_CHIEF',
          'MANAGER_SHIFT',
          'MANAGER_SHOP_ONLINE',
          'MANAGER_BILLING',
        ])
      ) {
        const children = [
          // {
          //   name: "Resumen",
          //   href: block ? "#" : "/billing/resume",
          //   current: pathname.includes("/billing/resume"),
          //   block,
          // },
          {
            name: 'Registros',
            href: block ? '#' : '/billing/registers',
            current: pathname.includes('/billing/registers'),
            block,
          },
          // {
          //   name: 'Análisis',
          //   href: block ? '#' : '/billing/analysis',
          //   current: pathname.includes('/billing/analysis'),
          //   block,
          // },
          /*{
          name: 'Pagos anticipados',
          href: block ? '#' : '/billing/prepaid',
          current: pathname.includes('/billing/prepaid'),
          block,
        },
        {
          name: "Pagos vencidos",
          href: block ? "#" : "/billing/overdue_payments",
          current: pathname.includes("/billing/overdue_payments"),
          block,
        },*/
        ]

        const code = 'M_BILLING'
        const module = moduleStates.get(code)

        navigation.push({
          name: 'Facturación',
          icon: ClipboardDocumentListIcon,
          current: mainCurrent === 'billing',
          href: block ? '#' : 'billing',
          children,
          section: 'Finanzas',
          roles: [
            'OWNER',
            'ADMIN',
            'MANAGER_SALES',
            'MARKETING_SALES',
            'MANAGER_CHIEF',
            'MANAGER_SHIFT',
            'MANAGER_SHOP_ONLINE',
            'MANAGER_BILLING',
          ],
          ...(showButtonSave &&
            module && {
              id: module.id,
              isActive: module.isActive,
              isSensitive: module.isSensitive,
            }),
        })
      }
    }

    //Registros Beta
    if (
      allowRoles([
        'OWNER',
        'ADMIN',
        'MANAGER_SALES',
        'MARKETING_SALES',
        'MANAGER_CHIEF',
        'MANAGER_SHIFT',
        'MANAGER_SHIPPING',
      ])
    ) {
      const children = [
        {
          name: 'Todos',
          href: block ? '#' : '/registers-beta/all',
          current: pathname.includes('/registers-beta/all'),
          block,
        },
        ...(allowRoles(['OWNER', 'CASH_OPERATOR'])
          ? [
              {
                name: 'Reporte de caja',
                href: block ? '#' : '/cash-operations/report',
                current: pathname.includes('/cash-operations/report'),
                block,
              },
            ]
          : []),
        {
          name: 'Operaciones de caja',
          href: block ? '#' : '/cash-operations/all',
          current: pathname.includes('/cash-operations/all'),
          block,
        },
        {
          name: 'Pagos anticipados',
          href: block ? '#' : '/registers/prepaid-payments',
          current: pathname.includes('/registers/prepaid-payments'),
          block,
        },
        {
          name: 'Pagos recibidos',
          href: block ? '#' : '/registers/currency-payments',
          current: pathname.includes('/registers/currency-payments'),
          block,
        },
        {
          name: 'Análisis',
          href: block ? '#' : '/billing/analysis',
          current: pathname.includes('/billing/analysis'),
          block,
        },
      ]

      navigation.push({
        name: 'Registros',
        icon: ClipboardDocumentListIcon,
        current: mainCurrent === 'registers-beta',
        href: block ? '#' : 'registers-beta',
        section: 'Finanzas',
        children,
        roles: [
          'OWNER',
          'ADMIN',
          'MANAGER_SALES',
          'MARKETING_SALES',
          'MANAGER_CHIEF',
          'MANAGER_SHIFT',
          'MANAGER_SHIPPING',
        ],

        // ...(showButtonSave &&
        //   module && {
        //     id: module.id,
        //     isActive: module.isActive,
        //     isSensitive: module.isSensitive,
        //   }),
      })
    }

    //Economic Cycles
    if (
      allowRoles([
        'OWNER',
        'ADMIN',
        'MANAGER_ECONOMIC_CYCLE',
        'MANAGER_SHIFT',
        'MANAGER_SALES',
        'MANAGER_CHIEF',
      ])
    ) {
      const children: NavChildren[] = []

      children.push(
        {
          name: 'Todos',
          href: '/ecocycle',
          current: mainCurrent === 'ecocycle' && !secondaryCurrent,
          block,
        },
        // {
        //   name: 'Cuentas por cobrar',
        //   href: '/ecocycle/accounts',
        //   current:
        //     mainCurrent === 'ecocycle' && secondaryCurrent === 'accounts',
        //   block,
        // },
        // {
        //   name: 'Cuentas por pagar',
        //   href: '/registers-beta/all?paymentStatus=PAYMENT_PENDING,PARTIALLY_PAID',
        //   current: false,
        //   block,
        // },
        {
          name: 'Análisis',
          href: '/ecocycle/analysis',
          current:
            mainCurrent === 'ecocycle' && secondaryCurrent === 'analysis',
          block,
        }
      )

      navigation.push({
        name: 'Ciclos económicos',
        icon: ArrowPathRoundedSquareIcon,
        current: pathname.includes('/ecocycle'),
        children,
        section: 'Finanzas',
        roles: [
          'OWNER',
          'ADMIN',
          'MANAGER_ECONOMIC_CYCLE',
          'MANAGER_SHIFT',
          'MANAGER_SALES',
          'MANAGER_CHIEF',
        ],
      })
    }

    //Tienda
    // if (
    //   process.env.REACT_APP_MODE !== 'LOCAL' &&
    //   (showButtonSave || isModuleActive('M_ONLINE_SHOP')) &&
    //   allowRoles(['OWNER', 'ADMIN', 'MANAGER_SHOP_ONLINE'])
    // ) {
    //   const code = 'M_ONLINE_SHOP'
    //   const module = moduleStates.get(code)

    //   navigation.push({
    //     name: 'Tienda online',
    //     icon: BuildingStorefrontIcon,
    //     current: mainCurrent === 'online_shop',
    //     href: '/online_shop',
    //     section: 'Finanzas',
    //     roles: ['OWNER', 'ADMIN', 'MANAGER_SHOP_ONLINE'],
    //     ...(showButtonSave &&
    //       module && {
    //         id: module.id,
    //         isActive: module.isActive,
    //         isSensitive: module.isSensitive,
    //       }),
    //   })
    // }

    //Cuentas bancarias
    if (
      (showButtonSave || isModuleActive('M_ACCOUNTS')) &&
      allowRoles(['MANAGER_CONTABILITY', 'AUDITOR_BANK_ACCOUNTS', 'OWNER'])
    ) {
      const children = [
        {
          name: 'Todas',
          href: '/bank_accounts',
          current: mainCurrent === 'bank_accounts' && !secondaryCurrent,
          block,
        },
        {
          name: 'Balance',
          href: '/bank_accounts/reports',
          current:
            mainCurrent === 'bank_accounts' && secondaryCurrent === 'reports',
          block,
        },
        {
          name: 'Mis listas',
          href: '/bank_accounts/lists',
          current:
            mainCurrent === 'bank_accounts' && secondaryCurrent === 'lists',
          block,
        },
        {
          name: 'Análisis',
          href: '/bank_accounts/analysis',
          current:
            mainCurrent === 'bank_accounts' && secondaryCurrent === 'analysis',
          block,
        },
      ]

      const code = 'M_ACCOUNTS'
      const module = moduleStates.get(code)

      navigation.push(
        //Bank Accounts
        {
          name: 'Cuentas bancarias',
          icon: CreditCardIcon,
          current: pathname.includes('/bank_accounts'),
          children,
          section: 'Finanzas',
          roles: ['MANAGER_CONTABILITY', 'AUDITOR_BANK_ACCOUNTS', 'OWNER'],
          ...(showButtonSave &&
            module && {
              id: module.id,
              isActive: module.isActive,
              isSensitive: module.isSensitive,
            }),
        }
      )
    }
    //Human Resources

    if (
      (showButtonSave || isModuleActive('M_HUMAN_RESOURCES')) &&
      allowRoles(['MANAGER_HUMAN_RESOURCES', 'OWNER'])
    ) {
      const children = [
        {
          name: 'Plantilla',
          href: '/human_resources/people',
          current:
            mainCurrent === 'human_resources' && secondaryCurrent === 'people',
          block,
        },
        {
          name: 'Categorías',
          href: '/human_resources/categories',
          current:
            mainCurrent === 'human_resources' &&
            secondaryCurrent === 'categories',
          block,
        },
        {
          name: 'Cargos',
          href: '/human_resources/post',
          current:
            mainCurrent === 'human_resources' && secondaryCurrent === 'post',
          block,
        },
        {
          name: 'Registro de asistencia',
          href: '/human_resources/access',
          current:
            mainCurrent === 'human_resources' && secondaryCurrent === 'access',
          block,
        },
      ]

      const code = 'M_HUMAN_RESOURCES'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Capital humano',
        icon: UserGroupIcon,
        current: pathname.includes('/human_resources'),
        children,
        section: 'Recursos Humanos',
        roles: ['MANAGER_HUMAN_RESOURCES', 'OWNER'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //salary

    if (
      (showButtonSave || isModuleActive('M_HUMAN_RESOURCES')) &&
      allowRoles(['MANAGER_SALARY_RULES', 'OWNER'])
    ) {
      const children = [
        {
          name: 'Generador',
          href: '/salary/generator',
          current: mainCurrent === 'salary' && secondaryCurrent === 'generator',
          block,
        },
        {
          name: 'Reglas',
          href: '/salary/rules',
          current: mainCurrent === 'salary' && secondaryCurrent === 'rules',
          block,
        },
        {
          name: 'Diarios',
          href: '/salary/daily',
          current: mainCurrent === 'salary' && secondaryCurrent === 'daily',
          block,
        },
        {
          name: 'Históricos',
          href: '/salary/historical',
          current:
            mainCurrent === 'salary' && secondaryCurrent === 'historical',
          block,
        },
        // {
        //   name: 'Análisis',
        //   href: '/salary/analysis',
        //   current: mainCurrent === 'salary' && secondaryCurrent === 'analysis',
        //   block,
        // },
      ]

      const code = 'M_HUMAN_RESOURCES'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Salarios',
        icon: BanknotesIcon,
        current: pathname.includes('/salary'),
        children,
        section: 'Recursos Humanos',
        roles: ['MANAGER_SALARY_RULES', 'OWNER'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Reservation
    // if (
    //   (showButtonSave || isModuleActive('M_BOOKING')) &&
    //   allowRoles(['OWNER'])
    // ) {
    //   const reservationChildrens = [
    //     {
    //       name: 'Calendario',
    //       href: block ? '#' : '/reservation/calendar',
    //       current: pathname.includes('/reservation/calendar'),
    //       block,
    //     },
    //     {
    //       name: 'Todas',
    //       href: block ? '#' : '/reservation/all',
    //       current: pathname.includes('/reservation/all'),
    //       block,
    //     },
    //   ]

    //   if (denyRoles(['AUDITOR'])) {
    //     reservationChildrens.push({
    //       name: 'Ajustes',
    //       href: block ? '#' : '/reservation/adjustment',
    //       current: pathname.includes('/reservation/adjustment'),
    //       block,
    //     })
    //   }

    //   const code = 'M_BOOKING'
    //   const module = moduleStates.get(code)

    //   navigation.push({
    //     name: 'Reservaciones',
    //     icon: CalendarIcon,
    //     current: mainCurrent === 'reservation',
    //     href: block ? '#' : 'reservation',
    //     section: 'Recursos Humanos',
    //     children: reservationChildrens,
    //     ...(showButtonSave &&
    //       module && {
    //         id: module.id,
    //         isActive: module.isActive,
    //         isSensitive: module.isSensitive,
    //       }),
    //   })
    // }

    //Clients
    if (allowRoles(['OWNER', 'MANAGER_CUSTOMERS'])) {
      navigation.push({
        name: 'Clientes',
        icon: BriefcaseIcon,
        current: pathname.includes('/clients'),
        href: '/clients',
        block,
        section: 'Fidelización',
        roles: ['OWNER', 'MANAGER_CUSTOMERS'],
        children: [
          {
            name: 'Todos',
            href: block ? '#' : '/clients/all',
            current: pathname.includes('/clients/all'),
            block,
          },
          {
            name: 'Categorías',
            href: block ? '#' : '/clients/categories',
            current: pathname.includes('/clients/categories'),
            block,
          },
        ],
      })
    }

    // Análisis
    if (allowRoles(['OWNER', 'ANALYSIS_REPORT']))
      navigation.push({
        name: 'Reportes',
        icon: ArrowTrendingUpIcon,
        current: pathname.includes('/reports'),
        href: block ? '#' : '/reports',
        block,
        section: 'Reportería',
        roles: ['OWNER', 'ANALYSIS_REPORT'],
        /*  children: [
          // {
          //   name: "Cierre contable (Beta)",
          //   href: block ? "#" : "/analysis/balance_sheet",
          //   current: pathname.includes("/analysis/balance_sheet"),
          //   block,
          // },
          {
            name: "Venta por productos",
            href: block ? "#" : "/analysis/selled_products",
            current: pathname.includes("/analysis/selled_products"),
            block,
          },
          {
            name: "Venta por órdenes",
            href: block ? "#" : "/analysis/sale_by_orders",
            current: pathname.includes("/analysis/sale_by_orders"),
            block,
          },
        ], */
      })

    //Cartelera digital
    if (
      (showButtonSave || isModuleActive('M_POSTER_MARKETING')) &&
      allowRoles(['OWNER', 'ADMIN', 'MANAGER_POSTER'])
    ) {
      const code = 'M_POSTER_MARKETING'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Cartelera digital',
        icon: PresentationChartBarIcon,
        current: mainCurrent === 'cart_digital',
        href: '/cart_digital',
        section: 'Marketing',
        roles: ['OWNER', 'ADMIN', 'MANAGER_POSTER'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Turns
    if (
      (showButtonSave || isModuleActive('F_SHIFTS')) &&
      allowRoles(['OWNER', 'MANAGER_COMMERCIAL_SHIFT'])
    ) {
      const children: NavChildren[] = []

      children.push({
        name: 'Todos',
        href: `/turns`,
        current: !secondaryCurrent && mainCurrent === 'turns',
        block,
      })

      const code = 'F_SHIFTS'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Turnos',
        icon: TicketIcon,
        current: mainCurrent === 'turns',
        block,
        section: 'Marketing',
        roles: ['OWNER', 'MANAGER_COMMERCIAL_SHIFT'],
        children,
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Users
    if (allowRoles(['OWNER', 'MANAGER_USERS', 'ADMIN'], true)) {
      navigation.push({
        name: 'Usuarios',
        icon: UsersIcon,
        current: pathname.includes('/users'),
        href: '/users/all',
        section: 'Administración',
        roles: ['OWNER', 'MANAGER_USERS'],
        // children: humanResourcesChildren_without_module_production,
      })
    }

    //Notifications
    if (
      showButtonSave ||
      (isModuleActive('M_NOTIFICATIONS') && allowRoles(['OWNER']))
    ) {
      const code = 'M_NOTIFICATIONS'
      const module = moduleStates.get(code)

      navigation.push({
        name: 'Notificaciones',
        icon: BellAlertIcon,
        href: '/notifications',
        current: mainCurrent === 'notifications',
        section: 'Administración',
        roles: ['OWNER'],
        ...(showButtonSave &&
          module && {
            id: module.id,
            isActive: module.isActive,
            isSensitive: module.isSensitive,
          }),
      })
    }

    //Config
    if (
      allowRoles([
        'OWNER',
        'MANAGER_CONFIGURATIONS',
        'MANAGER_CURRENCIES',
        'MANAGER_PRICES',
      ]) &&
      denyRoles(['AUDITOR'])
    ) {
      const children: NavChildren[] = []

      if (allowRoles(['OWNER', 'MANAGER_CONFIGURATIONS'], true)) {
        children.push(
          {
            name: 'Mi negocio',
            href: '/configurations/my_business',
            current: pathname.includes('/configurations/my_business'),
            block,
          },
          {
            name: 'Mis áreas',
            href: block ? '#' : '/configurations/my_areas',
            current: pathname.includes('/configurations/my_areas'),
            block,
          },
          {
            name: 'Áreas compartidas',
            href: block ? '#' : '/configurations/shared_areas',
            current: pathname.includes('/configurations/shared_areas'),
            block,
          }
        )
      }

      if (allowRoles(['MANAGER_PRICES'])) {
        children.push(
          {
            name: 'Sistemas de precios',
            href: block ? '#' : '/configurations/pricessystem',
            current: pathname.includes('/configurations/pricessystem'),
            block,
          }
          // {
          //   name: 'Módulos y servicios',
          //   href: block ? '#' : '/configurations/modules_services',
          //   current: pathname.includes('/configurations/modules_services'),
          //   block,
          // },
          // {
          //   name: 'Pasarelas de pago',
          //   href: block ? '#' : '/configurations/payment_gateways',
          //   current: pathname.includes('/configurations/payment_gateways'),
          //   block,
          // },
        )
      }

      if (allowRoles(['OWNER'], true)) {
        children.push({
          name: 'Pasarelas de pago',
          href: block ? '#' : '/configurations/payment_gateways',
          current: pathname.includes('/configurations/payment_gateways'),
          block,
        })
      }

      if (allowRoles(['OWNER', 'MANAGER_CONFIGURATIONS'], true)) {
        children.push({
          name: 'Categorías de costos',
          href: block ? '#' : 'configurations/fixedcosts',
          current: pathname.includes('configurations/fixedcosts'),
          block,
        })
      }

      if (allowRoles(['MANAGER_CURRENCIES'])) {
        children.push({
          name: 'Monedas',
          href: block ? '#' : '/configurations/currencies',
          current: pathname.includes('/configurations/currencies'),
          block,
        })
      }

      if (allowRoles(['OWNER', 'MANAGER_CONFIGURATIONS'], true)) {
        children.push(
          {
            name: 'Regiones de envíos',
            href: block ? '#' : '/configurations/regions',
            current: pathname.includes('/configurations/regions'),
            block,
          },
          {
            name: 'Ajustes',
            href: block ? '#' : '/configurations/settings',
            current: pathname.includes('/configurations/settings'),
            block,
          }
        )
      }

      navigation.push({
        name: 'Configuraciones',
        icon: Cog8ToothIcon,
        current: pathname.includes('configurations'),
        children,
        section: 'Administración',
        roles: [
          'OWNER',
          'MANAGER_CONFIGURATIONS',
          'MANAGER_CURRENCIES',
          'MANAGER_PRICES',
        ],
      })

      // navigation.push({
      //   name: 'Test Módulo',
      //   href: '/test',
      //   icon: HomeIcon,
      //   current: false,
      //   section: 'Administración',
      //   block: true, // fuerza que esté bloqueado
      // })
    }

    return navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // business,
    pathname,
    // plan,
    block,
    secondaryCurrent,
    mainCurrent,
    showButtonSave,
    moduleStates,
    allowRoles,
    denyRoles,
    isModuleActive,
    // hasModule,
  ])

  const [navigation, setNavigation] = useState<NavItem[]>([])

  const groupedNavigation = useMemo(() => {
    const source = showButtonSave ? navigation : navigationEditable

    return source?.reduce(
      (acc, item) => {
        const section = item.section || 'Otros'
        if (!acc[section]) acc[section] = []
        acc[section].push(item)
        return acc
      },
      {} as Record<string, NavItem[]>
    )
  }, [showButtonSave, navigation, navigationEditable])

  const [changeBusinessModal, setChangeBusinessModal] = useState(false)

  const [disclosure, setDisclosure] = useState<number | null>(null)

  const buildPackagesPayload = (navigation: NavItem[]) => {
    const seenIds = new Set<number>()
    const listPackages: { id: number; isActive: boolean }[] = []

    for (const item of navigation) {
      if (
        typeof item.id === 'number' &&
        typeof item.isActive === 'boolean' &&
        item.isSensitive === false &&
        !seenIds.has(item.id)
      ) {
        seenIds.add(item.id)
        listPackages.push({
          id: item.id,
          isActive: item.isActive,
        })
      }
    }

    return { listPackages }
  }

  const savePackages = () => {
    if (showButtonSave) {
      const payload = buildPackagesPayload(navigation)
      setIsSaving(true)
      editBusinessPackages(payload)
        .catch(() => {
          toast.error(
            'Upss, ha ocurrido un error inesperado. \n Intente de nuevo o consulte con su administrador...'
          )
        })
        .then(() => {
          refetchBusinessPackages()
          setShowButtonSave(false)
          toast.success('Modificado exitosamente')
          setDisclosure(-1)
        })
        .finally(() => setIsSaving(false))
    } else {
      setShowButtonSave(true)
      setNavigation(navigationEditable)
    }
  }

  useEffect(() => {
    if (showButtonSave) return

    const currentPath = location.pathname

    const hasAccessToCurrent = navigationEditable.some((item) => {
      const isAccessible =
        (item.isActive ?? true) &&
        !(item.isSensitive ?? false) &&
        allowRoles(item.roles ?? [], false)

      if (item.children && item.children.length > 0) {
        return (
          isAccessible &&
          item.children.some((child) => currentPath.startsWith(child.href))
        )
      }

      return isAccessible && currentPath.startsWith(item.href ?? '')
    })

    if (!hasAccessToCurrent) {
      for (const item of navigationEditable) {
        const isAccessible =
          (item.isActive ?? true) &&
          !(item.isSensitive ?? false) &&
          allowRoles(item.roles ?? [], false)

        if (!isAccessible) continue

        if (item.children && item.children.length > 0) {
          navigate(item.children[0].href, { replace: true })
          return
        }

        if (item.href) {
          navigate(item.href, { replace: true })
          return
        }
      }
    }
  }, [
    navigationEditable,
    // location.pathname,
    showButtonSave,
    allowRoles,
    navigate,
  ])

  const initializedEditRef = useRef(false)

  useEffect(() => {
    if (showButtonSave && !initializedEditRef.current) {
      setNavigation(navigationEditable)
      initializedEditRef.current = true
    }
    if (!showButtonSave) {
      initializedEditRef.current = false
    }
  }, [showButtonSave, navigationEditable])

  return (
    <>
      <Transition.Root as={Fragment} show={barState}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={() => switchSideBar()}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-800 pb-4 pt-5">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      className={cn(
                        'ml-1',
                        'flex h-10 w-10 items-center justify-center rounded-full',
                        'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                      )}
                      onClick={() => switchSideBar()}
                      type="button"
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        aria-hidden="true"
                        className="h-6 w-6 text-white"
                      />
                    </button>
                  </div>
                </Transition.Child>
                {allowRoles(['GROUP_OWNER'], true) &&
                branches &&
                branches?.length !== 0 ? (
                  <div className="flex items-center justify-start px-3 py-4 group-hover:w-60">
                    <ImageComponent
                      className="flex h-10 w-10 flex-shrink-0 overflow-hidden rounded-full"
                      hash={business?.logo?.blurHash ?? null}
                      src={business?.logo?.src ?? null}
                    />
                    <button
                      className="inline-flex w-full flex-1 items-center focus:outline-none"
                      onClick={() => setChangeBusinessModal(true)}
                    >
                      <h4 className="flex w-full p-2 text-gray-100">
                        {business?.name}
                      </h4>

                      <ChevronRightIcon className="h-5 text-white" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-start px-3 py-4 group-hover:w-64">
                    <ImageComponent
                      className="flex h-10 w-10 flex-shrink-0 overflow-hidden rounded-full"
                      hash={business?.logo?.blurHash ?? null}
                      src={business?.logo?.src ?? null}
                    />
                    <div className="inline-flex w-full items-center focus:outline-none group-hover:flex-shrink-0">
                      <h4 className="ml-2 flex items-center text-gray-100">
                        {business?.name}
                      </h4>
                    </div>
                  </div>
                )}
                <div className="flex flex-1 flex-col overflow-y-auto overflow-x-visible scrollbar-thin">
                  <nav className="flex-1 space-y-1 px-2 py-4">
                    {Object.entries(groupedNavigation ?? {}).map(
                      ([section, items]) => (
                        <div className="mt-4" key={section}>
                          <div className="py-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            {section}
                          </div>

                          {items.map((item, idxMaster) => {
                            const Icon = item.icon as React.ElementType
                            const keyId = `${section}-${idxMaster}`
                            const isCurrentSection =
                              item.current ||
                              item.children?.some((c) => c.current)
                            const isOpen =
                              //@ts-expect-error: ignore line
                              disclosure === keyId || isCurrentSection

                            return item.children === undefined ? (
                              <Link
                                className={classNames(
                                  item.current
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                  'group flex items-center rounded-md px-2 py-2 text-sm font-medium'
                                )}
                                key={item.name}
                                onClick={() => switchSideBar()}
                                to={item.href ?? ''}
                              >
                                {Icon && (
                                  <Icon
                                    aria-hidden="true"
                                    className={classNames(
                                      item.current
                                        ? 'text-gray-300'
                                        : 'text-gray-400 group-hover:text-gray-300',
                                      'mr-3 h-6 w-6 flex-shrink-0'
                                    )}
                                  />
                                )}
                                {item.name}
                              </Link>
                            ) : (
                              <div className="space-y-1" key={item.name}>
                                <button
                                  className={classNames(
                                    item.current
                                      ? 'bg-gray-900 text-white'
                                      : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                    'group flex w-full items-center rounded-md px-2 py-2 text-left text-sm',
                                    'font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500'
                                  )}
                                  onClick={() =>
                                    setDisclosure(
                                      //@ts-expect-error: ignore line
                                      disclosure === keyId ? null : keyId
                                    )
                                  }
                                  type="button"
                                >
                                  {Icon && (
                                    <Icon
                                      aria-hidden="true"
                                      className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                    />
                                  )}
                                  <span className="flex-1">{item.name}</span>
                                  <svg
                                    aria-hidden="true"
                                    className={classNames(
                                      isOpen
                                        ? 'rotate-90 text-gray-400'
                                        : 'text-gray-300',
                                      'ml-3 h-5 w-5 flex-shrink-0 transform transition-transform duration-150',
                                      'ease-in-out'
                                    )}
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      d="M6 6L14 10L6 14V6Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </button>

                                {isOpen && (
                                  <div className="space-y-1">
                                    {item.children?.map((subItem) => (
                                      <Link
                                        className={classNames(
                                          subItem.current
                                            ? 'bg-gray-700 text-white'
                                            : 'text-gray-400 hover:bg-gray-700 hover:text-white',
                                          'group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm',
                                          'font-medium'
                                        )}
                                        key={subItem.name}
                                        onClickCapture={() => switchSideBar()}
                                        to={subItem.href ?? ''}
                                      >
                                        {subItem.name}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )
                    )}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div aria-hidden="true" className="w-14 flex-shrink-0">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={cn(
          'group hidden transition-all duration-200 ease-in-out md:fixed md:inset-y-0 md:flex',
          staticBar ? 'md:w-64' : 'hover:w-64 active:w-64 md:w-20',
          'z-30 h-full shadow-[25px_0_25px_-20px_#10101048] md:flex-col md:pt-16'
        )}
        // onMouseLeave={() =>
        //   setDisclosure(navigation.findIndex((item) => item.current))
        // }
      >
        {/* Sidebar component, swap this element with another sidebar if you like */}

        <div className="relative flex min-h-0 flex-1 flex-col bg-slate-800">
          {isSaving && (
            <Fetching className="pointer-events-auto absolute inset-0 z-50 bg-white bg-opacity-60" />
          )}
          <div
            className={`flex flex-grow flex-col border-r border-slate-200 bg-slate-800 pb-4 pt-1 ${
              staticBar
                ? 'overflow-y-scroll pr-3 scrollbar-none'
                : 'group-hover:overflow-auto group-hover:pr-3 group-hover:scrollbar-none'
            }`}
          >
            {allowRoles(['GROUP_OWNER'], true) &&
            branches &&
            branches?.length !== 0 ? (
              <div
                className={`flex items-center justify-center px-3 py-4 ${
                  staticBar
                    ? 'w-full'
                    : 'group-hover:w-full group-hover:justify-start'
                }`}
              >
                <ImageComponent
                  className="flex h-10 w-10 flex-shrink-0 overflow-hidden rounded-full"
                  hash={business?.logo?.blurHash ?? null}
                  src={business?.logo?.src ?? null}
                />
                <button
                  className={cn(
                    staticBar
                      ? 'inline-flex'
                      : 'hidden group-hover:inline-flex',
                    'w-full flex-1 items-center focus:outline-none'
                  )}
                  onClick={() => setChangeBusinessModal(true)}
                >
                  <h4
                    className={cn(
                      staticBar ? 'flex' : 'hidden group-hover:flex',
                      'w-full p-2 text-gray-100'
                    )}
                  >
                    {business?.name}
                  </h4>

                  <ChevronRightIcon className="h-5 text-white" />
                </button>
              </div>
            ) : (
              <div
                className={`flex items-center justify-center px-3 py-4 ${
                  staticBar
                    ? 'w-full'
                    : 'group-hover:w-64 group-hover:justify-start'
                }`}
              >
                <ImageComponent
                  className="flex h-10 w-10 flex-shrink-0 overflow-hidden rounded-full"
                  hash={business?.logo?.blurHash ?? null}
                  src={business?.logo?.src ?? null}
                />
                <div
                  className={`${
                    staticBar
                      ? 'inline-flex'
                      : 'hidden group-hover:inline-flex group-hover:flex-shrink-0'
                  } w-full items-center focus:outline-none`}
                >
                  <h4
                    className={`${staticBar ? 'flex' : 'hidden group-hover:flex'} ml-2 items-center text-gray-100`}
                  >
                    {business?.name}
                  </h4>
                </div>
              </div>
            )}
            <div className="flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2 py-2">
                {Object.entries(groupedNavigation ?? {}).map(
                  ([section, items]) => (
                    <div className="mt-4" key={section}>
                      <div className="py-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                        {section}
                      </div>
                      {items.map((item, idxMaster) =>
                        item.children === undefined ? (
                          <div key={item.name}>
                            {showButtonSave ? (
                              <div
                                className={classNames(
                                  item.current
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                  `group relative flex items-center ${
                                    staticBar
                                      ? ''
                                      : 'justify-center group-hover:justify-start'
                                  } rounded-md px-2 py-2 text-sm font-medium`
                                )} // mismas clases que el Link
                                onClick={(e) => e.preventDefault()}
                              >
                                {(() => {
                                  const Icon = item.icon as
                                    | React.ElementType
                                    | undefined
                                  return Icon ? (
                                    <Icon
                                      aria-hidden="true"
                                      className={classNames(
                                        item.current
                                          ? 'text-gray-300'
                                          : 'text-gray-400 group-hover:text-gray-300',
                                        `${staticBar ? 'mr-3' : 'group-hover:mr-3'} h-6 w-6 flex-shrink-0`
                                      )}
                                    />
                                  ) : null
                                })()}
                                <div className="flex flex-row items-center">
                                  <span
                                    className={`${staticBar ? 'flex' : 'hidden group-hover:flex'} flex-shrink-0`}
                                  >
                                    {item.name}
                                  </span>
                                  {item.badge && (
                                    <span
                                      className={cn(
                                        staticBar
                                          ? 'flex'
                                          : 'hidden flex-shrink-0 group-hover:flex',
                                        item.badge.bgColor,
                                        item.badge.textColor,
                                        'ml-2 rounded-md px-2 py-1 text-xs'
                                      )}
                                    >
                                      {item.badge.name}
                                    </span>
                                  )}
                                </div>

                                {item.block && (
                                  <LockClosedIcon
                                    className={`${staticBar ? 'absolute' : 'hidden group-hover:absolute'} right-2 h-4`}
                                  />
                                )}

                                {typeof item.isActive === 'boolean' && (
                                  <div
                                    className="ml-auto"
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                    }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                  >
                                    <Switch
                                      checked={item.isActive}
                                      className={classNames(
                                        'absolute bottom-2 right-2',
                                        'data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-gray-200',
                                        item.isSensitive
                                          ? 'cursor-not-allowed opacity-50'
                                          : ''
                                      )}
                                      disabled={item.isSensitive}
                                      onCheckedChange={(val) => {
                                        const newNav = navigation.map(
                                          (navItem) =>
                                            navItem.id === item.id
                                              ? { ...navItem, isActive: val }
                                              : navItem
                                        )
                                        setNavigation(newNav)
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <Link
                                className={classNames(
                                  item.current
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                  `group relative flex items-center ${
                                    staticBar
                                      ? ''
                                      : 'justify-center group-hover:justify-start'
                                  } rounded-md px-2 py-2 text-sm font-medium`
                                )}
                                onClick={() => switchSideBar()}
                                to={item.href ?? ''}
                              >
                                {(() => {
                                  const Icon = item.icon as
                                    | React.ElementType
                                    | undefined
                                  return Icon ? (
                                    <Icon
                                      aria-hidden="true"
                                      className={classNames(
                                        item.current
                                          ? 'text-gray-300'
                                          : 'text-gray-400 group-hover:text-gray-300',
                                        `${staticBar ? 'mr-3' : 'group-hover:mr-3'} h-6 w-6 flex-shrink-0`
                                      )}
                                    />
                                  ) : null
                                })()}
                                <div className="flex flex-row items-center">
                                  <span
                                    className={`${staticBar ? 'flex' : 'hidden group-hover:flex'} flex-shrink-0`}
                                  >
                                    {item.name}
                                  </span>
                                  {item.badge && (
                                    <span
                                      className={cn(
                                        staticBar
                                          ? 'flex'
                                          : 'hidden flex-shrink-0 group-hover:flex',
                                        item.badge.bgColor,
                                        item.badge.textColor,
                                        'ml-2 rounded-md px-2 py-1 text-xs'
                                      )}
                                    >
                                      {item.badge.name}
                                    </span>
                                  )}
                                </div>
                                {item.block && (
                                  <LockClosedIcon
                                    className={`${staticBar ? 'absolute' : 'hidden group-hover:absolute'} right-2 h-4`}
                                  />
                                )}
                              </Link>
                            )}
                          </div>
                        ) : showButtonSave ? (
                          <div
                            className={classNames(
                              'group relative flex w-full items-center',
                              staticBar
                                ? ''
                                : 'justify-center group-hover:justify-start',
                              'rounded-md px-2 py-2 text-left text-sm font-medium text-gray-300 hover:bg-gray-700'
                            )}
                            key={item.name}
                          >
                            {(() => {
                              const Icon = item.icon as
                                | React.ElementType
                                | undefined
                              return Icon ? (
                                <Icon
                                  aria-hidden="true"
                                  className={classNames(
                                    item.current
                                      ? 'text-gray-300'
                                      : 'text-gray-400 group-hover:text-gray-300',
                                    `${staticBar ? 'mr-3' : 'group-hover:mr-3'} h-6 w-6 flex-shrink-0`
                                  )}
                                />
                              ) : null
                            })()}
                            <div className="flex flex-row items-center">
                              <span
                                className={`${staticBar ? 'flex' : 'hidden group-hover:flex'} flex-shrink-0`}
                              >
                                {item.name}
                              </span>
                              {item.badge && (
                                <span
                                  className={cn(
                                    staticBar
                                      ? 'flex'
                                      : 'hidden flex-shrink-0 group-hover:flex',
                                    item.badge.bgColor,
                                    item.badge.textColor,
                                    'ml-2 rounded-md px-2 py-1 text-xs'
                                  )}
                                >
                                  {item.badge.name}
                                </span>
                              )}
                            </div>

                            {typeof item.isActive === 'boolean' && (
                              <Switch
                                checked={item.isActive}
                                className={cn(
                                  'absolute right-2',
                                  'data-[state=checked]:bg-blue-600',
                                  'data-[state=unchecked]:bg-gray-200',
                                  item.isSensitive
                                    ? 'cursor-not-allowed opacity-50'
                                    : ''
                                )}
                                disabled={item.isSensitive}
                                onCheckedChange={(val) => {
                                  const newNav = navigation.map((navItem) =>
                                    navItem.id === item.id
                                      ? { ...navItem, isActive: val }
                                      : navItem
                                  )
                                  setNavigation(newNav)
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        ) : (
                          <Disclosure
                            as="div"
                            className="space-y-1"
                            defaultOpen={true}
                            key={item.name}
                          >
                            {({ open, close }) => {
                              if (open && disclosure !== idxMaster) close()
                              return (
                                <>
                                  <Disclosure.Button
                                    className={classNames(
                                      item.current
                                        ? 'bg-gray-900 text-white'
                                        : 'text-gray-300 hover:bg-gray-900 hover:text-white',
                                      `group relative flex w-full items-center ${
                                        staticBar
                                          ? ''
                                          : 'items-center justify-center group-hover:justify-start'
                                      } rounded-md px-2 py-2 text-left text-sm font-medium focus:outline-none`
                                    )}
                                    onClickCapture={() =>
                                      setDisclosure(idxMaster)
                                    }
                                  >
                                    <item.icon
                                      aria-hidden="true"
                                      className={classNames(
                                        item.current
                                          ? 'text-gray-300'
                                          : 'text-gray-400 group-hover:text-gray-300',
                                        `${staticBar ? 'mr-3' : 'group-hover:mr-3'} h-6 w-6 flex-shrink-0 text-center`
                                      )}
                                    />

                                    <div className="flex flex-row items-center">
                                      <span
                                        className={`${staticBar ? 'flex' : 'hidden flex-shrink-0 group-hover:flex'}`}
                                      >
                                        {item.name}
                                      </span>
                                      {item.badge && (
                                        <span
                                          className={cn(
                                            staticBar
                                              ? 'flex'
                                              : 'hidden flex-shrink-0 group-hover:flex',
                                            item.badge.bgColor,
                                            item.badge.textColor,
                                            'ml-2 rounded-md px-2 py-1 text-xs'
                                          )}
                                        >
                                          {item.badge.name}
                                        </span>
                                      )}
                                    </div>
                                    <ChevronRightIcon
                                      className={classNames(
                                        open
                                          ? 'rotate-90 text-gray-500'
                                          : 'text-gray-200',
                                        'h-4 w-4 flex-shrink-0 transform transition-colors duration-150 ease-in-out',
                                        'group-hover:text-gray-400',
                                        staticBar
                                          ? ''
                                          : 'hidden group-hover:block',
                                        'absolute right-1'
                                      )}
                                    />
                                  </Disclosure.Button>

                                  <Disclosure.Panel
                                    className={`${staticBar ? '' : 'hidden group-hover:block'} space-y-1 pl-4`}
                                  >
                                    {item.children &&
                                      item.children.map((subItem) => (
                                        <Link
                                          className={classNames(
                                            subItem.current
                                              ? 'bg-gray-600 text-white'
                                              : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                            'group relative flex w-full items-center',
                                            'rounded-md px-2 py-2 text-sm font-medium'
                                          )}
                                          key={subItem.name}
                                          to={subItem.href ?? ''}
                                        >
                                          <span className="flex flex-shrink-0">
                                            {subItem.name}
                                          </span>
                                          {subItem.block && (
                                            <LockClosedIcon className="absolute right-2 h-4" />
                                          )}
                                        </Link>
                                      ))}
                                  </Disclosure.Panel>
                                </>
                              )
                            }}
                          </Disclosure>
                        )
                      )}
                    </div>
                  )
                )}
              </nav>

              <div className="mt-10 flex items-center justify-center gap-4">
                {allowRoles(['OWNER', 'MANAGER_CONFIGURATIONS'], true) &&
                  (showButtonSave ? (
                    <BsFloppy
                      className={classNames(
                        'cursor-pointer text-sm text-gray-400 hover:text-gray-100',
                        showButtonSave ? 'text-blue-500' : ''
                      )}
                      onClick={savePackages}
                      title={'Guardar y salir del modo edición'}
                    />
                  ) : (
                    <BsPencil
                      className={classNames(
                        'cursor-pointer text-sm text-gray-400 hover:text-gray-100',
                        showButtonSave ? 'text-blue-500' : ''
                      )}
                      onClick={savePackages}
                      title={'Editar módulos'}
                    />
                  ))}

                {showButtonSave ? (
                  <BsXLg
                    className={classNames(
                      'cursor-pointer text-gray-400 hover:text-gray-100',
                      staticBar ? '' : 'rotate-90'
                    )}
                    onClick={() => setShowButtonSave(false)}
                    title="Salir del modo edición"
                  />
                ) : (
                  <BsPin
                    className={classNames(
                      'cursor-pointer text-gray-400 hover:text-gray-100',
                      staticBar ? '' : 'rotate-90'
                    )}
                    onClick={() => dispatch(changeStaticBar())}
                    title="Fijar barra lateral"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/**Ctrol Sidebar Button */}
      {!barState && (
        <button
          className={cn(
            'absolute top-16 z-50 mt-2 px-4 py-2 text-gray-600',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-500',
            'md:hidden'
          )}
          onClick={() => switchSideBar()}
          type="button"
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>
      )}

      {changeBusinessModal && (
        <Modal
          close={() => setChangeBusinessModal(false)}
          state={changeBusinessModal}
        >
          <BranchesModal closeModal={() => setChangeBusinessModal(false)} />
        </Modal>
      )}
    </>
  )
}

export default SideBar
